import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, RefreshCw, Download, FileText, FileImage } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/app/components/ui/pagination';
import { useJSAArchive } from '@/hooks/use-safety';
import { formatDateOnly } from '@/lib/date-utils';
import type { JSAArchiveRecordType } from '@/lib/types';
import { UploadLegacyJSADialog } from './upload-legacy-jsa-dialog';
import { apiClient } from '@/lib/api-client';
import { usePermissions } from '@/hooks/use-permissions';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending_signatures: 'bg-yellow-100 text-yellow-800',
  active: 'bg-green-100 text-green-800',
  closed: 'bg-blue-100 text-blue-800',
  archived: 'bg-gray-100 text-gray-600',
  legacy: 'bg-amber-100 text-amber-800',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  pending_signatures: 'Pending Signatures',
  active: 'Active',
  closed: 'Closed',
  archived: 'Archived',
  legacy: 'Legacy Scan',
};

const PAGE_SIZE = 25;

export function JSAArchivePage() {
  const { hasMinModuleRole } = usePermissions();
  const canManageSafety = hasMinModuleRole('safety', 'manager');
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');

  const page = parseInt(searchParams.get('page') || '1', 10);
  const status = searchParams.get('status') || '';
  const recordType = (searchParams.get('record_type') || '') as JSAArchiveRecordType | '';
  const q = searchParams.get('q') || '';

  const { data, isLoading, isError, refetch } = useJSAArchive({
    page,
    page_size: PAGE_SIZE,
    q,
    status,
    record_type: recordType,
  });

  const records = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const updateSearchParams = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      if (!updates.page) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleSearch = useCallback(() => {
    updateSearchParams({ q: searchInput, page: '1' });
  }, [searchInput, updateSearchParams]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const handleDownload = async (pdfUrl: string, title: string) => {
    try {
      const response = await apiClient.get(pdfUrl, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title.replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // Download failed silently
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">JSA Archive</h1>
          <p className="text-gray-600 mt-1">
            Searchable archive of digital and legacy scanned JSAs
            {totalCount > 0 && ` (${totalCount} total)`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {canManageSafety && (
            <UploadLegacyJSADialog />
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 flex gap-2">
              <Input
                placeholder="Search by title..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <Button variant="outline" onClick={handleSearch}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
            <Select
              value={status}
              onValueChange={(v) => updateSearchParams({ status: v === 'all' ? '' : v })}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_signatures">Pending Signatures</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
                <SelectItem value="legacy">Legacy Scan</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={recordType}
              onValueChange={(v) =>
                updateSearchParams({ record_type: v === 'all' ? '' : v })
              }
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="digital">Digital JSAs</SelectItem>
                <SelectItem value="legacy">Legacy Scans</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="text-center py-12 text-red-600">
          Failed to load archive. Please try again.
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && (
        <>
          {records.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No records found. Adjust your filters or upload a legacy JSA.
            </div>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((record) => (
                    <TableRow
                      key={`${record.record_type}-${record.id}`}
                      className={record.record_type === 'legacy' ? 'bg-amber-50/50' : ''}
                    >
                      <TableCell>
                        {record.record_type === 'legacy' ? (
                          <FileImage className="h-4 w-4 text-amber-600" />
                        ) : (
                          <FileText className="h-4 w-4 text-blue-600" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{record.title}</TableCell>
                      <TableCell>
                        {record.project_display
                          ? record.project_display.name
                          : record.job_display
                            ? record.job_display.name
                            : '—'}
                      </TableCell>
                      <TableCell>
                        {record.location_display ? record.location_display.name : '—'}
                      </TableCell>
                      <TableCell>
                        {formatDateOnly(record.date)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={statusColors[record.status || ''] || 'bg-gray-100'}
                        >
                          {statusLabels[record.status || ''] || record.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{record.created_by_username}</TableCell>
                      <TableCell className="text-right">
                        {record.pdf_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleDownload(record.pdf_url!, record.title)
                            }
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                {page > 1 && (
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => updateSearchParams({ page: String(page - 1) })}
                    />
                  </PaginationItem>
                )}
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                  const pageNum = start + i;
                  if (pageNum > totalPages) return null;
                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        isActive={pageNum === page}
                        onClick={() => updateSearchParams({ page: String(pageNum) })}
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                {page < totalPages && (
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => updateSearchParams({ page: String(page + 1) })}
                    />
                  </PaginationItem>
                )}
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}
    </div>
  );
}
